import type { RegroupingWorkshopConfig } from './RegroupingWorkshopConfig.js';
import {
  RegroupingWorkshopState,
  type RegroupingWorkshopActionResult,
  type RegroupingWorkshopSerializedState,
  type RegroupingWorkshopSnapshot,
  type RegroupingWorkshopSource,
  type RegroupingWorkshopTenSource,
} from './RegroupingWorkshopState.js';

export type RegroupingWorkshopLayoutMode = 'standard' | 'compact';

export interface RegroupingWorkshopVisibleBounds {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

export type RegroupingWorkshopCommand =
  | { readonly type: 'reveal-source'; readonly source: RegroupingWorkshopSource }
  | { readonly type: 'combine-one'; readonly source: RegroupingWorkshopSource; readonly amount?: number }
  | { readonly type: 'toggle-one-selection'; readonly pieceId: string }
  | { readonly type: 'clear-selection' }
  | { readonly type: 'regroup-selected-ones' }
  | { readonly type: 'combine-ten'; readonly source: RegroupingWorkshopTenSource; readonly amount?: number }
  | { readonly type: 'power-workshop' }
  | { readonly type: 'hint' }
  | { readonly type: 'reset' };

export interface RegroupingWorkshopCallbacks {
  readonly onStateChanged?: (snapshot: RegroupingWorkshopSnapshot) => void;
  readonly onFeedback?: (
    result: RegroupingWorkshopActionResult,
    snapshot: RegroupingWorkshopSnapshot,
  ) => void;
  readonly onComplete?: (
    config: RegroupingWorkshopConfig,
    snapshot: RegroupingWorkshopSnapshot,
  ) => void;
}

/**
 * Rendering-independent controller for mouse, touch/pointer, and keyboard views.
 * Relayout only changes the presentation mode; the mathematical state stays intact.
 */
export class RegroupingWorkshopChallenge {
  readonly state: RegroupingWorkshopState;
  private completionPublished: boolean;
  private layoutMode: RegroupingWorkshopLayoutMode = 'standard';
  private suspended = false;

  constructor(
    readonly config: RegroupingWorkshopConfig,
    private readonly callbacks: RegroupingWorkshopCallbacks = {},
    serialized?: RegroupingWorkshopSerializedState,
  ) {
    this.state = new RegroupingWorkshopState(config, serialized);
    this.completionPublished = serialized?.completed === true;
  }

  dispatch(command: RegroupingWorkshopCommand): RegroupingWorkshopActionResult | void {
    if (this.suspended && command.type !== 'reset') return;

    switch (command.type) {
      case 'reveal-source':
        return this.publishResult(this.state.revealSource(command.source));
      case 'combine-one':
        return this.publishResult(this.state.combineOne(command.source, command.amount));
      case 'toggle-one-selection':
        return this.publishResult(this.state.toggleOneSelection(command.pieceId));
      case 'clear-selection':
        return this.publishResult(this.state.clearSelection());
      case 'regroup-selected-ones':
        return this.publishResult(this.state.regroupSelectedOnes());
      case 'combine-ten':
        return this.publishResult(this.state.combineTen(command.source, command.amount));
      case 'power-workshop':
        return this.publishResult(this.state.powerWorkshop());
      case 'hint':
        this.state.requestHint();
        this.publish();
        return;
      case 'reset':
        this.completionPublished = false;
        this.state.reset();
        this.publish();
        return;
    }
  }

  layout(bounds: RegroupingWorkshopVisibleBounds) {
    const width = bounds.right - bounds.left;
    const height = bounds.bottom - bounds.top;
    this.layoutMode = width < 1100 || height < 640 ? 'compact' : 'standard';
    return this.layoutMode;
  }

  setSuspended(suspended: boolean) {
    this.suspended = suspended;
  }

  serialize() {
    return this.state.serialize();
  }

  get currentLayoutMode() { return this.layoutMode; }
  get snapshot() { return this.state.snapshot; }

  private publishResult(result: RegroupingWorkshopActionResult) {
    if (result === 'accepted' || result === 'ready' || result === 'complete') {
      this.publish();
    } else if (result !== 'invalid') {
      this.callbacks.onFeedback?.(result, this.state.snapshot);
    }
    return result;
  }

  private publish() {
    const snapshot = this.state.snapshot;
    this.callbacks.onStateChanged?.(snapshot);
    if (snapshot.isComplete && !this.completionPublished) {
      this.completionPublished = true;
      this.callbacks.onComplete?.(this.config, snapshot);
    }
  }
}
